import { useState } from 'react';
import { motion } from 'framer-motion';

interface ContactProps {
  city: string;
}

export function Contact({ city }: ContactProps) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    ville: city,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/arcachon-contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nom: '', prenom: '', email: '', telephone: '', ville: city, message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Demandez Votre Devis Gratuit
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Expert en réparation de fenêtres à {city}. Réponse rapide et intervention sous 24h.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="bg-teal rounded-3xl p-10 text-white h-full">
              <h3 className="text-2xl font-bold mb-8">Contactez-Nous</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">ZONE D'INTERVENTION</div>
                    <div className="text-lg font-medium">
                      {city} et tout le Bassin d'Arcachon
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">TÉLÉPHONE</div>
                    <a href="tel:+33554542545" className="text-lg font-medium hover:text-white/80 transition-colors cursor-pointer">
                      05 54 54 25 45
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">WHATSAPP</div>
                    <a href="https://wa.me/33745883392" className="text-lg font-medium hover:text-white/80 transition-colors cursor-pointer">
                      07 45 88 33 92
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">EMAIL</div>
                    <a href="mailto:contact@fenetrepro-arcachon.fr" className="text-lg font-medium hover:text-white/80 transition-colors cursor-pointer">
                      contact@fenetrepro-arcachon.fr
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">HORAIRES</div>
                    <div className="text-lg font-medium">
                      Lun-Sam : 8h-19h<br />
                      Urgences 7j/7
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <form
              id="arcachon-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-10 shadow-xl"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all"
                  />
                </div>
              </div>

              <input type="hidden" name="ville" value={city} />

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Décrivez votre besoin *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all resize-none"
                  placeholder="Type de fenêtre, problème rencontré, urgence..."
                ></textarea>
                <div className="text-sm text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 caractères
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-orange hover:bg-orange-dark text-white font-bold rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <span>Recevoir mon Devis Gratuit</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-teal/10 border border-teal rounded-xl text-teal text-center"
                >
                  <i className="ri-check-line text-xl mr-2"></i>
                  Demande envoyée ! Nous vous recontactons rapidement.
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-red-50 border border-red-300 rounded-xl text-red-600 text-center"
                >
                  <i className="ri-error-warning-line text-xl mr-2"></i>
                  Une erreur est survenue. Veuillez réessayer.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}